// Hook — Enregistre le token push FCM dans push_tokens au lancement
//
// iOS :  AppDelegate forward APNs → Firebase Messaging → dispatch FCM token via window event
// Android : PushNotifications.register() retourne directement un token FCM
// Web : no-op

import { useEffect } from 'react'
import { Capacitor } from '@capacitor/core'
import { PushNotifications } from '@capacitor/push-notifications'
import { supabase } from '@/lib/supabase'

declare global {
  interface Window {
    __fcmToken?: string
  }
}

/** iOS : attend le token FCM depuis le bridge natif (AppDelegate → window event) */
function waitForFCMToken(): Promise<string> {
  if (window.__fcmToken) return Promise.resolve(window.__fcmToken)

  return new Promise((resolve) => {
    const handler = (e: Event) => {
      const token = (e as CustomEvent).detail
      if (token && typeof token === 'string') {
        window.removeEventListener('fcmToken', handler)
        resolve(token)
      }
    }
    window.addEventListener('fcmToken', handler)

    setTimeout(() => {
      window.removeEventListener('fcmToken', handler)
      resolve('')
    }, 15000)
  })
}

/** Android : PushNotifications.register() retourne un token FCM directement */
function waitForAndroidToken(): Promise<string> {
  return new Promise((resolve) => {
    let resolved = false

    PushNotifications.addListener('registration', (token) => {
      if (resolved) return
      resolved = true
      resolve(token.value ?? '')
    })

    PushNotifications.addListener('registrationError', (err) => {
      if (resolved) return
      resolved = true
      console.error('[PushToken] Android registrationError:', JSON.stringify(err))
      resolve('')
    })

    PushNotifications.register()

    setTimeout(() => {
      if (resolved) return
      resolved = true
      resolve('')
    }, 15000)
  })
}

/** Upsert le token en base */
async function upsertToken(userId: string, fcmToken: string) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: existing } = await (supabase.from('push_tokens') as any)
    .select('id, token')
    .eq('user_id', userId)
    .maybeSingle()

  console.log('[PushToken] Existing row:', existing ? `id=${existing.id}` : 'none')

  if (existing) {
    if (existing.token !== fcmToken) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase.from('push_tokens') as any)
        .update({ token: fcmToken, updated_at: new Date().toISOString() })
        .eq('id', existing.id)
      if (error) console.error('[PushToken] Update error:', error.message)
      else console.log('[PushToken] Token updated in DB')
    } else {
      console.log('[PushToken] Token unchanged — skipping')
    }
  } else {
    console.log('[PushToken] Inserting new token...')
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error } = await (supabase.from('push_tokens') as any)
      .insert({ user_id: userId, token: fcmToken })
    if (error) console.error('[PushToken] Insert error:', error.message)
    else console.log('[PushToken] Token saved to DB')
  }
}

export function usePushTokenRegistration() {
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return

    let cleanup: (() => void) | undefined
    let cancelled = false
    const isIOS = Capacitor.getPlatform() === 'ios'

    const register = async (userId: string) => {
      try {
        console.log('[PushToken] Starting registration for user', userId.substring(0, 8), `(${Capacitor.getPlatform()})`)

        const permResult = await PushNotifications.requestPermissions()
        console.log('[PushToken] Permission result:', permResult.receive)
        if (permResult.receive !== 'granted') return

        let fcmToken: string

        if (isIOS) {
          // iOS : register déclenche APNs, AppDelegate forward à Firebase,
          // Firebase dispatch le token FCM via window event
          await PushNotifications.register()
          console.log('[PushToken] register() called — waiting for FCM token (iOS)...')
          fcmToken = await waitForFCMToken()
        } else {
          // Android : register retourne directement un token FCM
          console.log('[PushToken] Waiting for FCM token (Android)...')
          fcmToken = await waitForAndroidToken()
        }

        if (!fcmToken) {
          console.error('[PushToken] No FCM token received (timeout)')
          return
        }
        console.log('[PushToken] FCM token received:', fcmToken.substring(0, 30) + '...')

        await upsertToken(userId, fcmToken)

        // Écouter les rafraîchissements futurs
        if (isIOS) {
          const refreshHandler = async (e: Event) => {
            if (cancelled) return
            const newToken = (e as CustomEvent).detail
            console.log('[PushToken] Token refreshed (iOS):', newToken?.substring(0, 30))
            if (!newToken || newToken === fcmToken) return
            await upsertToken(userId, newToken)
          }
          window.addEventListener('fcmToken', refreshHandler)
          cleanup = () => window.removeEventListener('fcmToken', refreshHandler)
        }
      } catch (e) {
        const msg = e instanceof Error ? e.message : JSON.stringify(e)
        console.error('[PushToken] Registration failed:', msg, e)
      }
    }

    const tryRegister = async () => {
      const { data: { session } } = await supabase.auth.getSession()
      if (session?.user?.id) {
        console.log('[PushToken] Session found immediately')
        register(session.user.id)
        return true
      }
      return false
    }

    tryRegister().then(found => {
      if (found || cancelled) return

      console.log('[PushToken] No session yet — waiting for auth state change...')
      const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
        if (cancelled) return
        if (event === 'SIGNED_IN' && session?.user?.id) {
          console.log('[PushToken] Auth state SIGNED_IN — registering now')
          subscription.unsubscribe()
          register(session.user.id)
        }
      })

      const prev = cleanup
      cleanup = () => { prev?.(); subscription.unsubscribe() }
    })

    return () => { cancelled = true; cleanup?.() }
  }, [])
}
